import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { gsrApi, type GsrItem } from './api.ts';
import { useAuthStore } from '../../store/auth.ts';
import { t } from '../../lib/strings.ts';

interface Props {
  gsrId: string;
  items: GsrItem[];
  onCreated: () => void;
}

// Shown to the stockkeeper once a GOODS GSR is approved and no PR exists yet.
// They enter a manual PR number and the CURRENT STOCK for every item (mandatory);
// only then does «ފޯމް ތައްޔާރު ކުރޭ» generate the Purchase Request. The section is
// carried over from the GSR automatically, so it isn't asked for here.
export function PrPrepareForm({ gsrId, items, onCreated }: Props): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [prNumber, setPrNumber] = useState('');
  const [stock, setStock] = useState<Record<string, string>>({});
  // Default to the current user when they are themselves a stockkeeper (auto-fetch).
  const [stockkeeperById, setStockkeeperById] = useState(me?.role === 'STOCKKEEPER' ? me.id : '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Only STOCKKEEPER accounts appear in the dropdown.
  const { data: stockkeepers = [] } = useQuery({
    queryKey: ['user-options', 'STOCKKEEPER'],
    queryFn: () => gsrApi.userOptions('STOCKKEEPER'),
  });

  const setItemStock = (id: string, value: string) =>
    setStock((prev) => ({ ...prev, [id]: value }));

  const handleGenerate = async () => {
    setError(null);
    if (!prNumber.trim()) { setError(t.prPrepare.errorNumberRequired); return; }
    if (!stockkeeperById) { setError(t.prPrepare.errorStockkeeperRequired); return; }
    // Every item needs a non-negative integer stock value.
    const parsed: Array<{ gsrItemId: string; qtyInStock: number }> = [];
    for (const item of items) {
      const raw = stock[item.id];
      if (raw == null || raw === '') { setError(t.prPrepare.errorStockRequired); return; }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0) { setError(t.prPrepare.errorStockRequired); return; }
      parsed.push({ gsrItemId: item.id, qtyInStock: n });
    }

    setSubmitting(true);
    try {
      await gsrApi.createPurchaseRequest(gsrId, { prNumber: prNumber.trim(), stockkeeperById, stock: parsed });
      onCreated();
    } catch (err) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(code === 'pr_number_taken' ? t.prPrepare.errorNumberTaken : t.prPrepare.errorFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">{t.prPrepare.intro}</p>

      {/* PR number — manual, required */}
      <div className="space-y-1 max-w-xs">
        <label className="field-label" htmlFor="prNumber">{t.prPrepare.numberLabel}</label>
        <input
          id="prNumber"
          className="input"
          placeholder={t.prPrepare.numberPlaceholder}
          value={prNumber}
          onChange={(e) => setPrNumber(e.target.value)}
        />
      </div>

      {/* Items with mandatory current-stock inputs */}
      <div className="card-paper overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-muted-foreground">
              <th className="px-4 py-3 text-start font-medium">{t.prPrepare.colNum}</th>
              <th className="px-4 py-3 text-start font-medium">{t.prPrepare.colDescription}</th>
              <th className="px-4 py-3 text-start font-medium">{t.prPrepare.colQtyRequired}</th>
              <th className="px-4 py-3 text-start font-medium">{t.prPrepare.colStock}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((item, idx) => (
              <tr key={item.id}>
                <td className="px-4 py-3 num text-muted-foreground">{idx + 1}</td>
                <td className="px-4 py-3 font-medium">{item.particulars}</td>
                <td className="px-4 py-3 num">{item.qtyRequested}</td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    min={0}
                    required
                    className="input w-24"
                    placeholder={t.prPrepare.colStock}
                    aria-label={`${t.prPrepare.colStock} — ${item.particulars}`}
                    value={stock[item.id] ?? ''}
                    onChange={(e) => setItemStock(item.id, e.target.value)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Stockkeeper in charge — only STOCKKEEPER accounts */}
      <div className="space-y-1 max-w-xs">
        <label className="field-label" htmlFor="stockkeeper">{t.prPrepare.stockkeeperLabel}</label>
        <select
          id="stockkeeper"
          required
          className="input"
          value={stockkeeperById}
          onChange={(e) => setStockkeeperById(e.target.value)}
        >
          <option value="" disabled>{t.prPrepare.selectStockkeeper}</option>
          {stockkeepers.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {/* Generate — below the table */}
      <button
        type="button"
        onClick={() => void handleGenerate()}
        disabled={submitting}
        className="btn-primary"
      >
        {submitting ? t.prPrepare.generating : t.prPrepare.generate}
      </button>
    </div>
  );
}
